import { PokemonType } from "types/battle-engine/battle";
import { StatusEffect } from "types/battle-engine/battle";
import { ItemCardDefinition, ItemCardTier } from "types/item-cards/item-card-definition";

/**
 * core-item-deck-elixir (C1) static item-card catalog (~71 items).
 *
 * Numbers are provisional (playtest), per the spec. `engineSupported: true`
 * means `game/duel-engine/utils/resolve-item-effect.ts` has a real dispatch
 * for this card's `effectKind`; `false` means the card is fully real for
 * deckbuilding/ownership but is a no-op when played live (regen-over-time,
 * lifesteal, revive, random effects, stacking buffs, and other mechanics
 * with no existing engine primitive — see the build plan for the full list).
 * Cards with a partial primitive (e.g. "heal + cleanse") pick ONE dominant
 * effect to dispatch and are still marked supported; the other half of the
 * description is not applied — noted inline where relevant.
 *
 * Non-typed items (Free/Sustain/Buff) don't have a spec-assigned energy
 * type; this catalog interprets their "Energy" column as generic `NORMAL`
 * energy (closest existing analogue to a colorless/generic cost).
 */

const GENERIC_ENERGY_TYPE = PokemonType.NORMAL;

// ============================================================================
// Free — 0 elixir / 0 energy (12)
// ============================================================================

const FREE_ITEMS: ItemCardDefinition[] = [
  {
    key: "oran_berry",
    name: "Oran Berry",
    tier: ItemCardTier.FREE,
    cost: 0,
    energyCost: {},
    effectKind: "heal",
    magnitude: 5,
    description: "Heal 5%",
    engineSupported: true,
  },
  {
    key: "cheri_berry",
    name: "Cheri Berry",
    tier: ItemCardTier.FREE,
    cost: 0,
    energyCost: {},
    effectKind: "cleanse",
    magnitude: 1,
    description: "Remove 1 debuff from each poke",
    engineSupported: true,
  },
  {
    key: "war_cry",
    name: "War Cry",
    tier: ItemCardTier.FREE,
    cost: 0,
    energyCost: {},
    effectKind: "buff",
    statusEffect: StatusEffect.SPEED_UP,
    magnitude: 10,
    durationSeconds: 3,
    description: "+10% move speed, 3s",
    engineSupported: true,
  },
  {
    key: "focus_mark",
    name: "Focus Mark",
    tier: ItemCardTier.FREE,
    cost: 0,
    energyCost: {},
    effectKind: "unsupported",
    description: "+10% damage on current focused target, 4s",
    engineSupported: false,
    focusDependent: true,
  },
  {
    key: "aspear_berry",
    name: "Aspear Berry",
    tier: ItemCardTier.FREE,
    cost: 0,
    energyCost: {},
    effectKind: "unsupported",
    description: "Immune to slow/freeze, 3s",
    engineSupported: false,
  },
  {
    key: "rare_candy",
    name: "Rare Candy",
    tier: ItemCardTier.FREE,
    cost: 0,
    energyCost: {},
    effectKind: "unsupported",
    description: "+5% all stats, 3s",
    engineSupported: false,
  },
  {
    key: "healing_powder",
    name: "Healing Powder",
    tier: ItemCardTier.FREE,
    cost: 0,
    energyCost: {},
    effectKind: "unsupported",
    description: "Heal 3%/s for 3s",
    engineSupported: false,
  },
  {
    key: "leppa_berry",
    name: "Leppa Berry",
    tier: ItemCardTier.FREE,
    cost: 0,
    energyCost: {},
    effectKind: "unsupported",
    description: "Reduce skill cooldowns by 1s",
    engineSupported: false,
  },
  {
    key: "quick_feet",
    name: "Quick Feet",
    tier: ItemCardTier.FREE,
    cost: 0,
    energyCost: {},
    effectKind: "unsupported",
    description: "+10% attack speed, 3s",
    engineSupported: false,
  },
  {
    key: "hard_shell",
    name: "Hard Shell",
    tier: ItemCardTier.FREE,
    cost: 0,
    energyCost: {},
    effectKind: "buff",
    statusEffect: StatusEffect.DEFENSE_UP,
    magnitude: 10,
    durationSeconds: 4,
    description: "+10% Def, 4s",
    engineSupported: true,
  },
  {
    key: "smoke",
    name: "Smoke",
    tier: ItemCardTier.FREE,
    cost: 0,
    energyCost: {},
    effectKind: "unsupported",
    description: "+10% dodge, 3s",
    engineSupported: false,
  },
  {
    key: "light_stone",
    name: "Light Stone",
    tier: ItemCardTier.FREE,
    cost: 0,
    energyCost: {},
    effectKind: "unsupported",
    description: "Immune to knockback, 4s",
    engineSupported: false,
  },
];

// ============================================================================
// Sustain (13)
// ============================================================================

const SUSTAIN_ITEMS: ItemCardDefinition[] = [
  {
    key: "potion",
    name: "Potion",
    tier: ItemCardTier.SUSTAIN,
    cost: 1,
    energyCost: { [GENERIC_ENERGY_TYPE]: 0 },
    effectKind: "heal",
    magnitude: 15,
    description: "Heal 15%",
    engineSupported: true,
  },
  {
    key: "super_potion",
    name: "Super Potion",
    tier: ItemCardTier.SUSTAIN,
    cost: 2,
    energyCost: { [GENERIC_ENERGY_TYPE]: 1 },
    effectKind: "heal",
    magnitude: 30,
    description: "Heal 30%",
    engineSupported: true,
  },
  {
    key: "hyper_potion",
    name: "Hyper Potion",
    tier: ItemCardTier.SUSTAIN,
    cost: 3,
    energyCost: { [GENERIC_ENERGY_TYPE]: 2 },
    effectKind: "heal",
    magnitude: 50,
    description: "Heal 50%",
    engineSupported: true,
  },
  {
    key: "sitrus_berry",
    name: "Sitrus Berry",
    tier: ItemCardTier.SUSTAIN,
    cost: 2,
    energyCost: { [GENERIC_ENERGY_TYPE]: 1 },
    effectKind: "heal",
    magnitude: 10,
    durationSeconds: 5,
    description: "Heal 10% + regen 2%/s, 5s (regen not yet engine-supported)",
    engineSupported: true,
  },
  {
    key: "lum_berry",
    name: "Lum Berry",
    tier: ItemCardTier.SUSTAIN,
    cost: 1,
    energyCost: { [GENERIC_ENERGY_TYPE]: 0 },
    effectKind: "cleanse",
    magnitude: 0,
    description: "Full cleanse",
    engineSupported: true,
  },
  {
    key: "full_heal",
    name: "Full Heal",
    tier: ItemCardTier.SUSTAIN,
    cost: 2,
    energyCost: { [GENERIC_ENERGY_TYPE]: 1 },
    effectKind: "cleanse",
    magnitude: 0,
    durationSeconds: 4,
    description: "Cleanse + debuff immunity, 4s (immunity not yet engine-supported)",
    engineSupported: true,
  },
  {
    key: "leftovers",
    name: "Leftovers",
    tier: ItemCardTier.SUSTAIN,
    cost: 2,
    energyCost: { [GENERIC_ENERGY_TYPE]: 1 },
    effectKind: "unsupported",
    description: "Regen 3%/s, 8s",
    engineSupported: false,
  },
  {
    key: "shield_item",
    name: "Shield",
    tier: ItemCardTier.SUSTAIN,
    cost: 2,
    energyCost: { [GENERIC_ENERGY_TYPE]: 1 },
    effectKind: "shield",
    magnitude: 15,
    durationSeconds: 5,
    description: "Small shield, 5s",
    engineSupported: true,
  },
  {
    key: "barrier",
    name: "Barrier",
    tier: ItemCardTier.SUSTAIN,
    cost: 3,
    energyCost: { [GENERIC_ENERGY_TYPE]: 2 },
    effectKind: "shield",
    magnitude: 25,
    durationSeconds: 5,
    description: "Medium shield, 5s",
    engineSupported: true,
  },
  {
    key: "focus_band",
    name: "Focus Band",
    tier: ItemCardTier.SUSTAIN,
    cost: 3,
    energyCost: { [GENERIC_ENERGY_TYPE]: 2 },
    effectKind: "unsupported",
    description: "For 4s, pokes survive lethal hit at 1 HP (timing clutch)",
    engineSupported: false,
  },
  {
    key: "vital_essence",
    name: "Vital Essence",
    tier: ItemCardTier.SUSTAIN,
    cost: 4,
    energyCost: { [GENERIC_ENERGY_TYPE]: 2 },
    effectKind: "heal",
    magnitude: 25,
    description: "Heal 25% + cleanse (cleanse not yet engine-supported alongside heal)",
    engineSupported: true,
  },
  {
    key: "revive",
    name: "Revive",
    tier: ItemCardTier.SUSTAIN,
    cost: 4,
    energyCost: { [GENERIC_ENERGY_TYPE]: 2 },
    effectKind: "unsupported",
    description: "Raise all fallen with 25% HP",
    engineSupported: false,
  },
  {
    key: "sturdy_revive",
    name: "Sturdy Revive",
    tier: ItemCardTier.SUSTAIN,
    cost: 4,
    energyCost: { [GENERIC_ENERGY_TYPE]: 3 },
    effectKind: "unsupported",
    description: "Raise fallen with 40% HP + 2s shield",
    engineSupported: false,
  },
];

// ============================================================================
// Buffs (10)
// ============================================================================

const BUFF_ITEMS: ItemCardDefinition[] = [
  {
    key: "vitamin",
    name: "Vitamin",
    tier: ItemCardTier.BUFF,
    cost: 1,
    energyCost: { [GENERIC_ENERGY_TYPE]: 0 },
    effectKind: "buff",
    statusEffect: StatusEffect.ATTACK_UP,
    magnitude: 10,
    durationSeconds: 999, // "rest of round" placeholder -- no round-boundary system yet
    description: "+10% Atk for rest of round",
    engineSupported: true,
  },
  {
    key: "agile_boots",
    name: "Agile Boots",
    tier: ItemCardTier.BUFF,
    cost: 1,
    energyCost: { [GENERIC_ENERGY_TYPE]: 0 },
    effectKind: "buff",
    statusEffect: StatusEffect.SPEED_UP,
    magnitude: 50,
    durationSeconds: 4,
    description: "+50% move speed, 4s",
    engineSupported: true,
  },
  {
    key: "x_attack",
    name: "X-Attack",
    tier: ItemCardTier.BUFF,
    cost: 2,
    energyCost: { [GENERIC_ENERGY_TYPE]: 1 },
    effectKind: "buff",
    statusEffect: StatusEffect.ATTACK_UP,
    magnitude: 30,
    durationSeconds: 6,
    description: "+30% Atk, 6s",
    engineSupported: true,
  },
  {
    key: "x_defense",
    name: "X-Defense",
    tier: ItemCardTier.BUFF,
    cost: 2,
    energyCost: { [GENERIC_ENERGY_TYPE]: 1 },
    effectKind: "buff",
    statusEffect: StatusEffect.DEFENSE_UP,
    magnitude: 30,
    durationSeconds: 6,
    description: "+30% Def, 6s",
    engineSupported: true,
  },
  {
    key: "x_speed",
    name: "X-Speed",
    tier: ItemCardTier.BUFF,
    cost: 2,
    energyCost: { [GENERIC_ENERGY_TYPE]: 1 },
    effectKind: "unsupported",
    description: "+30% atk speed, 6s",
    engineSupported: false,
  },
  {
    key: "sure_hit",
    name: "Sure Hit",
    tier: ItemCardTier.BUFF,
    cost: 2,
    energyCost: { [GENERIC_ENERGY_TYPE]: 1 },
    effectKind: "unsupported",
    description: "+crit chance, 6s",
    engineSupported: false,
  },
  {
    key: "scope_lens",
    name: "Scope Lens",
    tier: ItemCardTier.BUFF,
    cost: 2,
    energyCost: { [GENERIC_ENERGY_TYPE]: 1 },
    effectKind: "unsupported",
    description: "Attacks ignore % of target Def, 6s",
    engineSupported: false,
  },
  {
    key: "power_amulet",
    name: "Power Amulet",
    tier: ItemCardTier.BUFF,
    cost: 2,
    energyCost: { [GENERIC_ENERGY_TYPE]: 1 },
    effectKind: "unsupported",
    description: "Next skill of each poke has doubled effect",
    engineSupported: false,
  },
  {
    key: "metronome",
    name: "Metronome",
    tier: ItemCardTier.BUFF,
    cost: 3,
    energyCost: { [GENERIC_ENERGY_TYPE]: 1 },
    effectKind: "unsupported",
    description: "Strong random buff",
    engineSupported: false,
  },
  {
    key: "banner",
    name: "Banner",
    tier: ItemCardTier.BUFF,
    cost: 4,
    energyCost: { [GENERIC_ENERGY_TYPE]: 2 },
    effectKind: "buff",
    statusEffect: StatusEffect.ATTACK_UP,
    magnitude: 20,
    secondaryStatusEffect: StatusEffect.DEFENSE_UP,
    secondaryMagnitude: 20,
    durationSeconds: 8,
    description: "+20% Atk and Def, 8s",
    engineSupported: true,
  },
];

// ============================================================================
// Typed — 2 per type (36)
// ============================================================================

interface TypedItemSpec {
  type: PokemonType;
  a: Omit<ItemCardDefinition, "key" | "tier" | "cost" | "energyCost" | "type">;
  b: Omit<ItemCardDefinition, "key" | "tier" | "cost" | "energyCost" | "type">;
}

const TYPED_SPECS: TypedItemSpec[] = [
  {
    type: PokemonType.FIRE,
    a: {
      name: "Charcoal",
      effectKind: "buff",
      statusEffect: StatusEffect.ATTACK_UP,
      magnitude: 30,
      durationSeconds: 6,
      description: "+30% Atk, 6s",
      engineSupported: true,
    },
    b: {
      name: "Inner Flame",
      effectKind: "buff",
      statusEffect: StatusEffect.ATTACK_UP,
      magnitude: 50,
      durationSeconds: 8,
      description: "+50% Atk, 8s",
      engineSupported: true,
    },
  },
  {
    type: PokemonType.WATER,
    a: {
      name: "Mystic Water",
      effectKind: "heal",
      magnitude: 15,
      description: "Heal 15% + 15% Atk (Atk portion not yet engine-supported)",
      engineSupported: true,
    },
    b: {
      name: "Tide",
      effectKind: "buff",
      statusEffect: StatusEffect.SPEED_UP,
      magnitude: 30,
      durationSeconds: 8,
      description: "Strong regen + move speed, 8s (regen not yet engine-supported)",
      engineSupported: true,
    },
  },
  {
    type: PokemonType.GRASS,
    a: {
      name: "Moss",
      effectKind: "unsupported",
      description: "Regen 3%/s, 8s",
      engineSupported: false,
    },
    b: {
      name: "Photosynthesis",
      effectKind: "cleanse",
      magnitude: 0,
      durationSeconds: 8,
      description: "Regen 5%/s + continuous cleanse, 8s (regen not yet engine-supported)",
      engineSupported: true,
    },
  },
  {
    type: PokemonType.ELECTRIC,
    a: {
      name: "Battery",
      effectKind: "unsupported",
      description: "+40% atk speed, 6s",
      engineSupported: false,
    },
    b: {
      name: "Overcharge",
      effectKind: "unsupported",
      description: "+60% atk speed, no skill CD, 5s",
      engineSupported: false,
    },
  },
  {
    type: PokemonType.ICE,
    a: {
      name: "Ice Crystal",
      effectKind: "shield",
      magnitude: 15,
      durationSeconds: 5,
      description: "Shield, 5s",
      engineSupported: true,
    },
    b: {
      name: "Absolute Zero",
      effectKind: "shield",
      magnitude: 25,
      durationSeconds: 6,
      description: "Strong shield + immune to knockback, 6s (immunity not yet engine-supported)",
      engineSupported: true,
    },
  },
  {
    type: PokemonType.FIGHTING,
    a: {
      name: "Black Belt",
      effectKind: "unsupported",
      description: "+Atk as HP drops",
      engineSupported: false,
    },
    b: {
      name: "Fighting Spirit",
      effectKind: "unsupported",
      description: "+crit and lifesteal, 6s",
      engineSupported: false,
    },
  },
  {
    type: PokemonType.POISON,
    a: {
      name: "Gland",
      effectKind: "unsupported",
      description: "Lifesteal 15%, 8s",
      engineSupported: false,
    },
    b: {
      name: "Miasma",
      effectKind: "unsupported",
      description: "Lifesteal 25%, 6s",
      engineSupported: false,
    },
  },
  {
    type: PokemonType.GROUND,
    a: {
      name: "Firm Ground",
      effectKind: "buff",
      statusEffect: StatusEffect.DEFENSE_UP,
      magnitude: 20,
      durationSeconds: 8,
      description: "+Def + anti-displacement, 8s (anti-displacement not yet engine-supported)",
      engineSupported: true,
    },
    b: {
      name: "Tremor",
      effectKind: "buff",
      statusEffect: StatusEffect.DEFENSE_UP,
      magnitude: 35,
      durationSeconds: 6,
      description: "Strong +Def + immune to displacement, 6s (immunity not yet engine-supported)",
      engineSupported: true,
    },
  },
  {
    type: PokemonType.FLYING,
    a: {
      name: "Swift Feather",
      effectKind: "buff",
      statusEffect: StatusEffect.SPEED_UP,
      magnitude: 20,
      durationSeconds: 6,
      description: "+move speed + dodge, 6s",
      engineSupported: true,
    },
    b: {
      name: "Updraft",
      effectKind: "unsupported",
      description: "High dodge + instant reposition to focused target, 5s",
      engineSupported: false,
      focusDependent: true,
    },
  },
  {
    type: PokemonType.PSYCHIC,
    a: {
      name: "Twisted Spoon",
      effectKind: "unsupported",
      description: "−30% skill CD, 8s",
      engineSupported: false,
    },
    b: {
      name: "Premonition",
      effectKind: "unsupported",
      description: "−50% CD + next skill doubled, 6s",
      engineSupported: false,
    },
  },
  {
    type: PokemonType.BUG,
    a: {
      name: "Cocoon",
      effectKind: "shield",
      magnitude: 15,
      durationSeconds: 4,
      description: "Shield that explodes into +Atk (explode not yet engine-supported)",
      engineSupported: true,
    },
    b: {
      name: "Swarm",
      effectKind: "unsupported",
      description: "+Atk stacking per hit dealt, 8s",
      engineSupported: false,
    },
  },
  {
    type: PokemonType.ROCK,
    a: {
      name: "Solid Rock",
      effectKind: "unsupported",
      description: "Reduces heavy hits, 8s",
      engineSupported: false,
    },
    b: {
      name: "Rampart",
      effectKind: "unsupported",
      description: "Near-immune 3s, then −Def 2s",
      engineSupported: false,
    },
  },
  {
    type: PokemonType.GHOST,
    a: {
      name: "Shadow Cloak",
      effectKind: "unsupported",
      description: "+40% dodge, 4s",
      engineSupported: false,
    },
    b: {
      name: "Possession",
      effectKind: "unsupported",
      description: "Intangible 3s, then debuff immunity 3s",
      engineSupported: false,
    },
  },
  {
    type: PokemonType.DRAGON,
    a: {
      name: "Fang",
      effectKind: "unsupported",
      description: "+Atk stacking over time, 10s",
      engineSupported: false,
    },
    b: {
      name: "Draconic Fury",
      effectKind: "unsupported",
      description: "+Atk/atk speed growing up to 12s",
      engineSupported: false,
    },
  },
  {
    type: PokemonType.DARK,
    a: {
      name: "Night Visor",
      effectKind: "unsupported",
      description: "+crit, 6s",
      engineSupported: false,
    },
    b: {
      name: "Ambush",
      effectKind: "unsupported",
      description: "High +crit + first hit from behind doubled, 6s",
      engineSupported: false,
    },
  },
  {
    type: PokemonType.STEEL,
    a: {
      name: "Coating",
      effectKind: "buff",
      statusEffect: StatusEffect.DEFENSE_UP,
      magnitude: 20,
      durationSeconds: 8,
      description: "Flat armor, 8s",
      engineSupported: true,
    },
    b: {
      name: "Steel Fortress",
      effectKind: "buff",
      statusEffect: StatusEffect.DEFENSE_UP,
      magnitude: 30,
      durationSeconds: 6,
      description: "Armor + reflects % damage, 6s (reflect not yet engine-supported)",
      engineSupported: true,
    },
  },
  {
    type: PokemonType.FAIRY,
    a: {
      name: "Fairy Dust",
      effectKind: "heal",
      magnitude: 12,
      description: "Heal 12% + cleanse (cleanse not yet engine-supported alongside heal)",
      engineSupported: true,
    },
    b: {
      name: "Blessing",
      effectKind: "heal",
      magnitude: 20,
      durationSeconds: 5,
      description: "Heal + debuff immunity, 5s (immunity not yet engine-supported)",
      engineSupported: true,
    },
  },
  {
    type: PokemonType.NORMAL,
    a: {
      name: "Amulet",
      effectKind: "buff",
      statusEffect: StatusEffect.ATTACK_UP,
      magnitude: 12,
      secondaryStatusEffect: StatusEffect.DEFENSE_UP,
      secondaryMagnitude: 12,
      durationSeconds: 6,
      description: "+12% all stats, 6s (approximated as Atk+Def)",
      engineSupported: true,
    },
    b: {
      name: "Adaptability",
      effectKind: "buff",
      statusEffect: StatusEffect.ATTACK_UP,
      magnitude: 25,
      secondaryStatusEffect: StatusEffect.DEFENSE_UP,
      secondaryMagnitude: 25,
      durationSeconds: 5,
      description: "+25% all stats, 5s (approximated as Atk+Def)",
      engineSupported: true,
    },
  },
];

const TYPED_ITEMS: ItemCardDefinition[] = TYPED_SPECS.flatMap(({ type, a, b }) => [
  {
    key: `${type.toLowerCase()}_a_${a.name.toLowerCase().replace(/\s+/g, "_")}`,
    tier: ItemCardTier.TYPED,
    cost: 2,
    energyCost: { [type]: 2 },
    type,
    ...a,
  },
  {
    key: `${type.toLowerCase()}_b_${b.name.toLowerCase().replace(/\s+/g, "_")}`,
    tier: ItemCardTier.TYPED,
    cost: 3,
    energyCost: { [type]: 3 },
    type,
    ...b,
  },
]);

export const ITEM_CARD_DEFINITIONS: ItemCardDefinition[] = [
  ...FREE_ITEMS,
  ...SUSTAIN_ITEMS,
  ...BUFF_ITEMS,
  ...TYPED_ITEMS,
];
